import React from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Container,
  Stack,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

import useClientPhotos from "../../../../../hooks/useClientPhotos";
import useOrganizationUsers from "../../../../../hooks/useOrganizationUsers";
import { groupFormattedPhotosIntoGroups } from "../../../../../utils/groupProgressPhotosForDisplay";
import MediaProgressPhotoClientSection from "./MediaProgressPhotoClientSection";
import ProgressPhotosPrivacyGate from "../../../../../components/molecules/ProgressPhotosPrivacyGate";
import organizationIdState from "../../../../../atoms/organizationIdAtom";

/**
 * Full progress-photo tools for one client — responsive header (back + wrapping title).
 */
const MediaProgressPhotoClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const organizationId = useRecoilValue(organizationIdState);

  const users = useOrganizationUsers({ organizationId });

  const {
    startingPhotos,
    progressPhotos,
    loading,
    formatPhotosForDisplay,
    startingPhotoGroupId,
  } = useClientPhotos(clientId);

  const clientDisplayName = React.useMemo(() => {
    const u = users?.find((x) => x.uid === clientId || x.id === clientId);
    if (u?.firstName || u?.lastName) {
      return `${u.firstName || ""} ${u.lastName || ""}`.trim();
    }
    return clientId;
  }, [users, clientId]);

  const formattedProgress = React.useMemo(
    () =>
      progressPhotos && formatPhotosForDisplay
        ? formatPhotosForDisplay(progressPhotos)
        : [],
    [progressPhotos, formatPhotosForDisplay],
  );

  const formattedStarting = React.useMemo(
    () =>
      startingPhotos && formatPhotosForDisplay
        ? formatPhotosForDisplay(startingPhotos)
        : [],
    [startingPhotos, formatPhotosForDisplay],
  );

  const flatPhotos = React.useMemo(() => {
    const byId = new Map();
    [...formattedProgress, ...formattedStarting].forEach((p) => {
      if (!byId.has(p.id)) byId.set(p.id, p);
    });
    return [...byId.values()];
  }, [formattedProgress, formattedStarting]);

  const groupedPhotos = React.useMemo(
    () => groupFormattedPhotosIntoGroups(flatPhotos, startingPhotoGroupId),
    [flatPhotos, startingPhotoGroupId],
  );

  const handleBack = React.useCallback(() => {
    navigate("/home/shared-videos", {
      state: { mediaTab: "progressPhotos" },
    });
  }, [navigate]);

  if (!clientId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Missing client.</Typography>
        <Button onClick={handleBack}>Back</Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          py: { xs: 2, sm: 2.5 },
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
            <Button
              color="inherit"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.35)",
                px: 1.5,
                maxWidth: "100%",
              }}
            >
              All clients
            </Button>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.75rem", md: "2rem" },
                lineHeight: { xs: 1.35, sm: 1.25 },
                fontWeight: 700,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {clientDisplayName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.92,
                lineHeight: 1.45,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              Progress photos — compare, download, and AI analysis
            </Typography>
          </Stack>
        </Container>
      </Box>

      {loading && !progressPhotos && !startingPhotos ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            px: { xs: 1.25, sm: 2 },
            py: { xs: 1.5, sm: 2 },
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <ProgressPhotosPrivacyGate resetKey={clientId}>
            <MediaProgressPhotoClientSection
              clientId={clientId}
              clientDisplayName={clientDisplayName}
              organizationId={organizationId}
              flatPhotos={flatPhotos}
              groupedPhotos={groupedPhotos}
              galleryVariant="compact"
              referenceGroupId={startingPhotoGroupId}
            />
          </ProgressPhotosPrivacyGate>
        </Box>
      )}
    </Box>
  );
};

export default MediaProgressPhotoClientDetailPage;
